import React, { useState } from "react";
import axios from "axios";
import { API, useAuth } from "@/App";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { toast } from "sonner";
import { Pencil } from "lucide-react";
import CloudinaryUpload from "@/components/CloudinaryUpload";
import { Field, ImagePicker } from "./LinkImporter";
import { apiError } from "@/lib/apiError";

const fromProduct = (p) => ({
  title: p.title || "", brand_name: p.brand_name || "", category: p.category || "", description: p.description || "",
  images: [...new Set([p.image_url, ...(p.images || [])].filter(Boolean))], original_price: p.original_price ?? "",
  discounted_price: p.discounted_price ?? "", affiliate_link: p.affiliate_link || "", cashback_percent: p.cashback_percent ?? "",
  commission_percent: p.commission_percent ?? "", rating: p.rating ?? "", review_count: p.review_count ?? "",
  highlights: (p.highlights || []).join("\n"),
});

const num = (v) => (v === "" || v === null || v === undefined ? null : parseFloat(v));

export const ProductEditDialog = ({ product, open, onOpenChange, categories, onSaved }) => {
  const { getAuthHeaders } = useAuth();
  const [form, setForm] = useState(() => (product ? fromProduct(product) : null));
  const [saving, setSaving] = useState(false);
  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));
  if (!product || !form) return null;

  const save = async () => {
    if (!form.title || !num(form.original_price) || !form.images.length || !form.category) {
      toast.error("Title, price, category and at least one image are required");
      return;
    }
    setSaving(true);
    try {
      await axios.put(`${API}/admin/products/affiliate/${product.id}`, {
        title: form.title, brand_name: form.brand_name, category: form.category, description: form.description || form.title,
        image_url: form.images[0], images: form.images, original_price: num(form.original_price), discounted_price: num(form.discounted_price),
        affiliate_link: form.affiliate_link, cashback_percent: num(form.cashback_percent) || 0, commission_percent: num(form.commission_percent) || 0,
        rating: num(form.rating), review_count: form.review_count === "" ? null : parseInt(form.review_count, 10),
        highlights: form.highlights.split("\n").map((h) => h.trim()).filter(Boolean).slice(0, 8),
      }, { headers: getAuthHeaders() });
      toast.success("Product updated");
      onSaved?.();
      onOpenChange(false);
    } catch (err) {
      toast.error(apiError(err, "Failed to update product"));
    }
    setSaving(false);
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto w-[calc(100%-2rem)] rounded-3xl" data-testid="product-edit-dialog">
        <DialogHeader><DialogTitle className="flex items-center gap-2"><Pencil className="w-5 h-5 text-primary-600" />Edit product</DialogTitle></DialogHeader>
        <div className="space-y-4">
          <ImagePicker images={form.images} onChange={(v) => set("images", v)} />
          <div className="grid sm:grid-cols-2 gap-3">
            <Field label="Add image URL"><Input placeholder="Paste image link + Enter" onKeyDown={(e) => { if (e.key === "Enter" && e.currentTarget.value) { e.preventDefault(); set("images", [...form.images, e.currentTarget.value]); e.currentTarget.value = ""; } }} data-testid="edit-add-image-input" /></Field>
            <Field label="Or upload a photo"><CloudinaryUpload value="" onChange={(url) => url && set("images", [...form.images, url])} folder="products/" resourceType="image" testId="edit-image-upload" /></Field>
          </div>
          <div className="grid sm:grid-cols-2 gap-3">
            <Field label="Title"><Input value={form.title} onChange={(e) => set("title", e.target.value)} data-testid="edit-title-input" /></Field>
            <Field label="Brand"><Input value={form.brand_name} onChange={(e) => set("brand_name", e.target.value)} data-testid="edit-brand-input" /></Field>
            <Field label="MRP / Original price (₹)"><Input type="number" value={form.original_price} onChange={(e) => set("original_price", e.target.value)} data-testid="edit-mrp-input" /></Field>
            <Field label="Selling price (₹, optional)"><Input type="number" value={form.discounted_price} onChange={(e) => set("discounted_price", e.target.value)} data-testid="edit-price-input" /></Field>
            <Field label="Category">
              <Select value={form.category} onValueChange={(v) => set("category", v)}>
                <SelectTrigger data-testid="edit-category-select"><SelectValue placeholder="Select" /></SelectTrigger>
                <SelectContent>{categories.map((c) => <SelectItem key={c.id} value={c.name}>{c.name}</SelectItem>)}</SelectContent>
              </Select>
            </Field>
            <Field label="Affiliate link"><Input value={form.affiliate_link} onChange={(e) => set("affiliate_link", e.target.value)} data-testid="edit-affiliate-input" /></Field>
            <Field label="Cashback %"><Input type="number" value={form.cashback_percent} onChange={(e) => set("cashback_percent", e.target.value)} data-testid="edit-cashback-input" /></Field>
            <Field label="Commission %"><Input type="number" value={form.commission_percent} onChange={(e) => set("commission_percent", e.target.value)} data-testid="edit-commission-input" /></Field>
            <Field label="Rating (0-5)"><Input type="number" step="0.1" value={form.rating} onChange={(e) => set("rating", e.target.value)} data-testid="edit-rating-input" /></Field>
            <Field label="Ratings count"><Input type="number" value={form.review_count} onChange={(e) => set("review_count", e.target.value)} data-testid="edit-review-count-input" /></Field>
          </div>
          <Field label="Description"><Textarea rows={4} value={form.description} onChange={(e) => set("description", e.target.value)} data-testid="edit-description-input" /></Field>
          <Field label="Highlights (one per line)"><Textarea rows={3} value={form.highlights} onChange={(e) => set("highlights", e.target.value)} data-testid="edit-highlights-input" /></Field>
          <div className="flex justify-end gap-2">
            <Button variant="outline" onClick={() => onOpenChange(false)} data-testid="edit-cancel-btn">Cancel</Button>
            <Button onClick={save} disabled={saving} className="bg-secondary-500 !text-primary-900 hover:bg-secondary-400 font-bold" data-testid="edit-save-btn">{saving ? "Saving..." : "Save changes"}</Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
};
